/**
 * WeeklyMacroCalendar — HERO card com macros de HOJE em destaque
 * + strip compacta de 7 dias em baixo para contexto semanal.
 *
 * Props:
 *   program: "keto_fusion" | "desafio_8k"
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Dumbbell, Leaf, Flame } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const DAY_LABELS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const DAY_FULL = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

function isoDow(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.getDay() === 0 ? 6 : d.getDay() - 1; // Mon=0..Sun=6
}

export default function WeeklyMacroCalendar({ program = "keto_fusion" }) {
  const [days, setDays] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/programs/${program}/macros/week`, { headers: authHeaders() });
        const data = await res.json();
        setDays(data.days || []);
      } catch {
        setDays([]);
      }
    })();
  }, [program]);

  if (!days.length) return null;

  const today = days.find((d) => d.is_today) || days[0];
  const isTraining = today.is_training_day;
  const isRefeed = today.is_refeed_day;
  const focusColor = isTraining ? "#84cc16" : (isRefeed ? "#f59e0b" : "#f97316");
  const focusLabel = isTraining ? "Dia de treino" : (isRefeed ? "Refeed programado" : "Dia de recuperação");
  const FocusIcon = isTraining ? Dumbbell : Leaf;
  const dowLabel = DAY_FULL[isoDow(today.date)];

  return (
    <div className="space-y-3" data-testid="weekly-macro-calendar">
      {/* HERO — HOJE em destaque */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border p-5 relative overflow-hidden"
        style={{
          borderColor: `${focusColor}55`,
          background: `linear-gradient(140deg, ${focusColor}18 0%, rgba(9,9,11,0.95) 60%)`,
          boxShadow: `0 6px 22px ${focusColor}25, inset 0 1px 0 ${focusColor}25`,
        }}
        data-testid="today-macros-hero"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: focusColor }}>
              Hoje · {dowLabel}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <FocusIcon className="w-4 h-4" style={{ color: focusColor }} />
              <p className="text-base font-black text-zinc-100">{focusLabel}</p>
            </div>
          </div>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `radial-gradient(circle, ${focusColor}40 0%, ${focusColor}10 70%)`,
              boxShadow: `0 0 18px ${focusColor}55, inset 0 0 12px ${focusColor}30`,
            }}
          >
            <Flame className="w-5 h-5" style={{ color: focusColor }} />
          </div>
        </div>

        {/* Macro Grid grande */}
        <div className="grid grid-cols-4 gap-2">
          <div className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Kcal</p>
            <p className="text-lg font-black tabular-nums" style={{ color: focusColor }}>
              {today.target_kcal ?? "–"}
            </p>
          </div>
          <div className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Carbs</p>
            <p className="text-lg font-black text-zinc-100 tabular-nums">
              {today.target_carbs_g ?? 0}<span className="text-[10px] text-zinc-500 font-normal">g</span>
            </p>
          </div>
          <div className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Prot</p>
            <p className="text-lg font-black text-zinc-100 tabular-nums">
              {today.target_protein_g ?? 0}<span className="text-[10px] text-zinc-500 font-normal">g</span>
            </p>
          </div>
          <div className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Gord</p>
            <p className="text-lg font-black text-zinc-100 tabular-nums">
              {today.target_fat_g ?? 0}<span className="text-[10px] text-zinc-500 font-normal">g</span>
            </p>
          </div>
        </div>
      </motion.div>

      {/* STRIP compacta 7 dias */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-3.5 h-3.5 text-lime-400" />
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Semana em cycling</p>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {days.map((d, i) => {
            const training = d.is_training_day;
            const refeed = d.is_refeed_day;
            const isTodayCell = d.is_today;
            const c = training ? "#84cc16" : (refeed ? "#f59e0b" : "#f97316");
            const Icon = training ? Dumbbell : Leaf;
            const dayLabel = DAY_LABELS[isoDow(d.date)];
            return (
              <motion.div
                key={d.date}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="p-1.5 rounded-lg border relative overflow-hidden"
                style={{
                  borderColor: isTodayCell ? c : `${c}30`,
                  background: isTodayCell ? `${c}18` : "transparent",
                  boxShadow: isTodayCell ? `0 0 12px ${c}45` : "none",
                }}
                data-testid={`calendar-day-${i}`}
              >
                <p className="text-[9px] uppercase tracking-wider text-zinc-500 text-center leading-none">{dayLabel}</p>
                <Icon className="w-2.5 h-2.5 mx-auto my-1" style={{ color: c }} />
                <p className="text-[10px] text-zinc-300 text-center font-mono tabular-nums leading-none">{d.target_kcal || "–"}</p>
                <p className="text-[8px] text-zinc-500 text-center mt-0.5">C{d.target_carbs_g || 0}g</p>
                {isTodayCell && (
                  <div className="absolute top-0.5 right-0.5 w-1 h-1 rounded-full animate-pulse" style={{ background: c }} />
                )}
              </motion.div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-3 text-[9px] text-zinc-500 justify-center">
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-lime-500" /> Treino</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Refeed</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-orange-500" /> Recuperação</span>
        </div>
      </div>
    </div>
  );
}
